/**
 * Configurable Business Rules System
 * 
 * This module provides a centralized system for managing business rules,
 * grace periods, thresholds, and other configurable parameters that
 * vary by business requirements.
 */

export interface AttendanceRulesConfig {
  gracePeriodsMinutes: {
    /** Grace period for considering an employee "on time" (default: 5 minutes) */
    onTimeGrace: number;
    /** Threshold for marking arrival as "too early" (default: 30 minutes) */
    tooEarlyThreshold: number;
    /** Threshold for early departure warnings (default: 15 minutes) */
    earlyDepartureThreshold: number;
  };
  workingHours: {
    /** Standard working hours per day (default: 8 hours) */
    standardWorkDayHours: number;
    /** Minutes threshold for overtime calculation (default: 480 = 8 hours) */
    overtimeThresholdMinutes: number;
    /** Standard working days per week (default: 5) */
    standardWorkDaysPerWeek: number;
  };
  lateArrival: {
    /** Hour after which arrivals are considered late (default: 9 = 9 AM) */
    lateAfterHour: number;
    /** Minutes threshold for late arrival marking (default: 5 minutes) */
    lateThresholdMinutes: number;
  };
}

export interface PerformanceThresholdsConfig {
  attendanceThresholds: {
    /** Percentage below which attendance is considered low (default: 80%) */
    lowAttendanceWarning: number;
    /** Percentage below which punctuality is considered poor (default: 85%) */
    poorPunctualityWarning: number;
    /** Percentage above which utilization is considered high risk (default: 110%) */
    highUtilizationWarning: number;
    /** Minutes above which overtime is considered significant (default: 120) */
    significantOvertimeMinutes: number;
    /** Number of early arrivals that triggers a warning (default: 2) */
    tooManyEarlyArrivals: number;
  };
  coverageStandards: {
    /** Coverage percentage for optimal status (default: 100%) */
    optimalCoverage: number;
    /** Coverage percentage for good status (default: 80%) */
    goodCoverage: number;
    /** Coverage percentage for acceptable status (default: 50%) */
    acceptableCoverage: number;
    /** Maximum unassigned shifts allowed for "good" status (default: 1) */
    maxUnassignedForGood: number;
  };
}

export interface AnalyticsConfig {
  /** Default time range in days for analytics queries (default: 30) */
  defaultTimeRangeDays: number;
  conversionRateBenchmarks: {
    /** Expected review conversion percentage (default: 5.2%) */
    reviewConversion: number;
    /** Expected hiring conversion percentage (default: calculated) */
    hiringConversion: number;
    /** Average time to hire in days (default: 14) */
    averageTimeToHireDays: number;
  };
  insights: {
    /** Minimum sample size for reliable insights (default: 10) */
    minSampleSize: number;
    /** Days to look back for trend analysis (default: 7) */
    trendAnalysisDays: number;
  };
}

export interface SchedulingConfig {
  /** Tolerance in minutes for gaps between shifts (default: 5) */
  gapToleranceMinutes: number;
  /** Maximum extension in minutes for overnight shifts (default: 180 = 3 hours) */
  maxOvernightExtensionMinutes: number;
  /** Buffer time in minutes for shift scheduling (default: 15) */
  scheduleBufferMinutes: number;
}

export interface BusinessRulesConfig {
  attendance: AttendanceRulesConfig;
  performance: PerformanceThresholdsConfig;
  analytics: AnalyticsConfig;
  scheduling: SchedulingConfig;
}

/**
 * Default business rules configuration
 * These are the recommended defaults that work for most businesses
 */
export const DEFAULT_BUSINESS_RULES: BusinessRulesConfig = {
  attendance: {
    gracePeriodsMinutes: {
      onTimeGrace: 5,
      tooEarlyThreshold: 30,
      earlyDepartureThreshold: 15,
    },
    workingHours: {
      standardWorkDayHours: 8,
      overtimeThresholdMinutes: 480, // 8 hours
      standardWorkDaysPerWeek: 5,
    },
    lateArrival: {
      lateAfterHour: 9, // 9 AM
      lateThresholdMinutes: 5,
    },
  },
  performance: {
    attendanceThresholds: {
      lowAttendanceWarning: 80, // 80%
      poorPunctualityWarning: 85, // 85%
      highUtilizationWarning: 110, // 110%
      significantOvertimeMinutes: 120, // 2 hours
      tooManyEarlyArrivals: 2,
    },
    coverageStandards: {
      optimalCoverage: 100, // 100%
      goodCoverage: 80, // 80%
      acceptableCoverage: 50, // 50%
      maxUnassignedForGood: 1,
    },
  },
  analytics: {
    defaultTimeRangeDays: 30,
    conversionRateBenchmarks: {
      reviewConversion: 5.2, // 5.2%
      hiringConversion: 15, // 15%
      averageTimeToHireDays: 14,
    },
    insights: {
      minSampleSize: 10,
      trendAnalysisDays: 7,
    },
  },
  scheduling: {
    gapToleranceMinutes: 5,
    maxOvernightExtensionMinutes: 180, // 3 hours
    scheduleBufferMinutes: 15,
  },
};

/**
 * Business Rules Manager
 * Handles loading, caching, and applying business rules for a specific business
 */
export class BusinessRulesManager {
  private rules: BusinessRulesConfig;
  private businessId: string;
  private cacheTimestamp: number;
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(businessId: string, customRules?: Partial<BusinessRulesConfig>) {
    this.businessId = businessId;
    this.rules = this.mergeWithDefaults(customRules);
    this.cacheTimestamp = Date.now();
  }

  /**
   * Merge custom rules with defaults
   */
  private mergeWithDefaults(customRules?: Partial<BusinessRulesConfig>): BusinessRulesConfig {
    if (!customRules) return { ...DEFAULT_BUSINESS_RULES };

    return {
      attendance: {
        gracePeriodsMinutes: {
          ...DEFAULT_BUSINESS_RULES.attendance.gracePeriodsMinutes,
          ...customRules.attendance?.gracePeriodsMinutes,
        },
        workingHours: {
          ...DEFAULT_BUSINESS_RULES.attendance.workingHours,
          ...customRules.attendance?.workingHours,
        },
        lateArrival: {
          ...DEFAULT_BUSINESS_RULES.attendance.lateArrival,
          ...customRules.attendance?.lateArrival,
        },
      },
      performance: {
        attendanceThresholds: {
          ...DEFAULT_BUSINESS_RULES.performance.attendanceThresholds,
          ...customRules.performance?.attendanceThresholds,
        },
        coverageStandards: {
          ...DEFAULT_BUSINESS_RULES.performance.coverageStandards,
          ...customRules.performance?.coverageStandards,
        },
      },
      analytics: {
        ...DEFAULT_BUSINESS_RULES.analytics,
        ...customRules.analytics,
        conversionRateBenchmarks: {
          ...DEFAULT_BUSINESS_RULES.analytics.conversionRateBenchmarks,
          ...customRules.analytics?.conversionRateBenchmarks,
        },
        insights: {
          ...DEFAULT_BUSINESS_RULES.analytics.insights,
          ...customRules.analytics?.insights,
        },
      },
      scheduling: {
        ...DEFAULT_BUSINESS_RULES.scheduling,
        ...customRules.scheduling,
      },
    };
  }

  /**
   * Get all business rules
   */
  getRules(): BusinessRulesConfig {
    return { ...this.rules };
  }

  /**
   * Get attendance-specific rules
   */
  getAttendanceRules(): AttendanceRulesConfig {
    return { ...this.rules.attendance };
  }

  /**
   * Get performance threshold rules
   */
  getPerformanceThresholds(): PerformanceThresholdsConfig {
    return { ...this.rules.performance };
  }

  /**
   * Get analytics configuration
   */
  getAnalyticsConfig(): AnalyticsConfig {
    return { ...this.rules.analytics };
  }

  /**
   * Get scheduling configuration
   */
  getSchedulingConfig(): SchedulingConfig {
    return { ...this.rules.scheduling };
  }

  /**
   * Update specific rules (partial update)
   */
  updateRules(updates: Partial<BusinessRulesConfig>): void {
    this.rules = this.mergeWithDefaults({ ...this.rules, ...updates });
    this.cacheTimestamp = Date.now();
  }

  /**
   * Check if cache is still valid
   */
  isCacheValid(): boolean {
    return Date.now() - this.cacheTimestamp < BusinessRulesManager.CACHE_DURATION;
  }

  /**
   * Helper methods for common rule checks
   */
  
  isOnTime(actualArrival: Date, scheduledStart: Date): boolean {
    const diffMinutes = (actualArrival.getTime() - scheduledStart.getTime()) / (1000 * 60);
    return Math.abs(diffMinutes) <= this.rules.attendance.gracePeriodsMinutes.onTimeGrace;
  }

  isTooEarly(actualArrival: Date, scheduledStart: Date): boolean {
    const diffMinutes = (scheduledStart.getTime() - actualArrival.getTime()) / (1000 * 60);
    return diffMinutes >= this.rules.attendance.gracePeriodsMinutes.tooEarlyThreshold;
  }

  isLate(actualArrival: Date, scheduledStart: Date): boolean {
    const diffMinutes = (actualArrival.getTime() - scheduledStart.getTime()) / (1000 * 60);
    return diffMinutes > this.rules.attendance.gracePeriodsMinutes.onTimeGrace;
  }

  isOvertime(hoursWorked: number): boolean {
    const overtimeHours = this.rules.attendance.workingHours.overtimeThresholdMinutes / 60;
    return hoursWorked > overtimeHours;
  }

  isSignificantOvertime(overtimeMinutes: number): boolean {
    return overtimeMinutes >= this.rules.performance.attendanceThresholds.significantOvertimeMinutes;
  }

  isLowAttendance(attendanceRate: number): boolean {
    return attendanceRate < this.rules.performance.attendanceThresholds.lowAttendanceWarning;
  }

  isPoorPunctuality(punctualityRate: number): boolean {
    return punctualityRate < this.rules.performance.attendanceThresholds.poorPunctualityWarning;
  }

  isHighUtilization(utilizationRate: number): boolean {
    return utilizationRate > this.rules.performance.attendanceThresholds.highUtilizationWarning;
  }

  getCoverageStatus(coveragePercentage: number, unassignedShifts: number): 'optimal' | 'good' | 'acceptable' | 'poor' {
    if (coveragePercentage >= this.rules.performance.coverageStandards.optimalCoverage && 
        unassignedShifts === 0) {
      return 'optimal';
    }
    
    if (coveragePercentage >= this.rules.performance.coverageStandards.goodCoverage && 
        unassignedShifts <= this.rules.performance.coverageStandards.maxUnassignedForGood) {
      return 'good';
    }
    
    if (coveragePercentage >= this.rules.performance.coverageStandards.acceptableCoverage) {
      return 'acceptable';
    }
    
    return 'poor';
  }

  getDefaultAnalyticsTimeRange(): number {
    return this.rules.analytics.defaultTimeRangeDays;
  }
}

// Global cache for business rules managers
const rulesManagerCache = new Map<string, BusinessRulesManager>();

/**
 * Get or create a BusinessRulesManager for a specific business
 */
export function getBusinessRulesManager(businessId: string, customRules?: Partial<BusinessRulesConfig>): BusinessRulesManager {
  const cacheKey = businessId;
  const existing = rulesManagerCache.get(cacheKey);
  
  if (existing && existing.isCacheValid()) {
    // Update with custom rules if provided
    if (customRules) {
      existing.updateRules(customRules);
    }
    return existing;
  }
  
  const manager = new BusinessRulesManager(businessId, customRules);
  rulesManagerCache.set(cacheKey, manager);
  return manager;
}

/**
 * Clear the cache for a specific business (useful when rules are updated)
 */
export function clearBusinessRulesCache(businessId: string): void {
  rulesManagerCache.delete(businessId);
}

/**
 * Clear all cached business rules
 */
export function clearAllBusinessRulesCache(): void {
  rulesManagerCache.clear();
}

// Export types for external use
export type {
  AttendanceRulesConfig,
  PerformanceThresholdsConfig,
  AnalyticsConfig,
  SchedulingConfig,
  BusinessRulesConfig,
};